// Wohler Chemical — Phase 5B Expansion Compounds (To-Do / Research-Needed)
// These compounds are added from the master RC list but require
// full graduate-level research, scoring, and synthesis plans.
// Each has status: "RESEARCH-NEEDED" and populated nextSteps for the Journal.
//
// Priority order: work these down the list with the founder.

// Template for default to-do next-steps (for reference when adding more compounds):
// - Complete literature review and populate publishedResearch section
// - Develop lab-scale synthesis route using Wohler standard solvents
// - Design kg-scale manufacturing plan with EHS assessment
// - Populate Wohler scoring (benefit, harm, evidence confidence)
// - Confirm federal & state scheduling status with regulatory attorney
// - Identify primary vendor / starting-material sources

export const todoCompounds = {

  // ========================================================================
  // TRYPTAMINES — Additional
  // ========================================================================
  "4-ho-det": {
    id: "4-ho-det",
    name: "4-HO-DET",
    fullName: "4-Hydroxy-N,N-diethyltryptamine",
    aliases: ["CZ-74", "Ethocin"],
    categoryId: "tryptamines",
    program: "Program B — Tryptamine Space",
    cas: "22204-89-3",
    formula: "C₁₄H₂₀N₂O",
    molecularWeight: "232.32 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled (DEA) — but structural analog of psilocin (Schedule I). Federal Analogue Act applies for human-consumption intent. Research-use-only posture required.",
    benefitScore: 72,
    harmScore: 25,
    evidenceConfidence: "MODERATE",
    status: "RESEARCH-NEEDED",
    statusColor: "orange",
    goDecision: "TODO — add to research journal pipeline",
    priorityTier: "Tier 2",
    summary: "4-HO-DET (Ethocin / CZ-74) is the desacetyl active metabolite of CZ-74 (4-AcO-DET). It was investigated in the 1960s by Sandoz alongside psilocybin by Hofmann and colleagues. Less studied in modern literature than 4-HO-MET but chemically closely related. Added to the Wohler pipeline as a companion to the existing 4-AcO-DET program.",
    pros: [
      "Classical Sandoz compound with a documented human-research history",
      "Shorter duration than psilocybin (~3–4 h)",
      "Direct active metabolite of 4-AcO-DET — useful PK reference"
    ],
    cons: [
      "Limited modern pharmacology data vs 4-HO-MET",
      "High structural similarity to psilocin — scrutiny risk"
    ],
    pharmacology: {
      targets: [
        { receptor: "5-HT2A", activity: "Agonist (full/partial TBD)", value: "Data pending" },
        { receptor: "5-HT2C", activity: "Agonist", value: "Data pending" }
      ],
      mechanism: "Serotonergic psychedelic — 5-HT2A agonism drives glutamate-mediated neuroplasticity"
    },
    labSynthesis: null,
    kgScaleSynthesis: null,
    safety: { sideEffects: ["Pending research"], acuteToxicity: "No formal LD50 data published" },
    publishedResearch: { keyFindings: ["Sandoz investigational compound 1960s — Hofmann et al."] },
    wohlerScoring: {
      benefitRationale: "Placeholder — needs literature review",
      harmRationale: "Placeholder — psilocin-analog scrutiny risk",
      evidenceBasis: "LOW — requires full citation review"
    },
    nextSteps: [
      "Locate original Sandoz Hofmann 1960s publications",
      "Compare HTR / ED50 data to 4-HO-MET",
      "Design Speeter-Anthony lab synthesis from 4-acetoxyindole + diethylamine",
      "Populate kg-scale plan parallel to 4-HO-MET route",
      "Finalize Wohler scoring"
    ]
  },

  // ========================================================================
  // MITRAGYNINE / MGM — Additional
  // ========================================================================
  "7-oh-mitragynine": {
    id: "7-oh-mitragynine",
    name: "7-OH-Mitragynine",
    fullName: "7-Hydroxymitragynine",
    aliases: ["7-OH", "7-OHMG"],
    categoryId: "mitragynine",
    program: "Program A — Kratom / Harm Reduction",
    cas: "174418-82-7",
    formula: "C₂₃H₃₀N₂O₅",
    molecularWeight: "414.50 g/mol",
    schedulingStatus: "monitor",
    schedulingNote: "Federal scheduling under active FDA/DEA review as of 2025. Several U.S. states (e.g., WV, IN) have moved to restrict 7-OH kratom products. Watch-only posture — confirm with regulatory counsel monthly. Not currently federally scheduled but high scheduling risk.",
    benefitScore: 70,
    harmScore: 55,
    evidenceConfidence: "HIGH",
    status: "HOLD",
    statusColor: "red",
    goDecision: "HOLD — high scheduling risk. Watch-only / pseudoreference compound.",
    priorityTier: "Watch",
    summary: "7-OH-Mitragynine is the oxidized active metabolite of mitragynine and the most potent kratom-derived mu-opioid agonist known (~10–13× morphine at MOR in vitro). It is the primary driver of kratom's opioid-like pharmacology in humans. High scheduling risk in 2025–2026 — FDA/DEA have flagged 7-OH-dominant kratom products as a public-health concern. Kept as a reference/research tool only.",
    pros: [
      "Most potent naturally-occurring kratom alkaloid at MOR",
      "Essential pharmacological reference for Program A analog SAR",
      "Well-characterized in published receptor-binding studies (Kruegel 2016)"
    ],
    cons: [
      "High abuse liability — closest kratom compound to classical opioid pharmacology",
      "Rapidly advancing federal & state scheduling risk",
      "FDA has specifically flagged 7-OH-dominant products as a safety concern"
    ],
    pharmacology: {
      targets: [
        { receptor: "MOR", activity: "Partial/full agonist", value: "Ki ≈ 13.5 nM; ~10–13× morphine potency (GTPγS)" },
        { receptor: "KOR", activity: "Weak agonist", value: "Ki ≈ 123 nM" },
        { receptor: "DOR", activity: "Weak agonist", value: "Ki ≈ 155 nM" }
      ],
      mechanism: "MOR agonist; active metabolite of mitragynine via CYP3A4 hydroxylation"
    },
    labSynthesis: null,
    kgScaleSynthesis: null,
    safety: {
      sideEffects: ["Respiratory depression at high dose (opioid-like)", "Constipation", "Physical dependence", "Withdrawal on discontinuation"],
      acuteToxicity: "Higher abuse liability than mitragynine parent",
      chronicRisks: "Opioid-like dependence profile in animal self-administration studies"
    },
    publishedResearch: {
      keyFindings: [
        "Kruegel et al. 2016 — receptor binding & functional data (JACS)",
        "Váradi et al. 2016 — mitragynine pseudoindoxyl & 7-OH comparative pharmacology",
        "Hiranita et al. 2022 — self-administration & abuse liability in rats"
      ],
      citations: []
    },
    wohlerScoring: {
      benefitRationale: "High scientific value as a reference compound for kratom/MGM pharmacology",
      harmRationale: "Significant abuse liability + scheduling-risk in 2025-26 make production risky",
      evidenceBasis: "HIGH — well characterized"
    },
    nextSteps: [
      "Monitor FDA/DEA regulatory status monthly",
      "Build citation library from Kruegel, Váradi, Hiranita",
      "Document oxidation procedure from mitragynine (OsO4 / m-CPBA route)",
      "Decision gate: keep as watch-only or drop from program"
    ]
  },

  "mgm-15": {
    id: "mgm-15",
    name: "MGM-15",
    fullName: "MGM-15 (fluorinated mitragynine analog — structure pending publication)",
    aliases: [],
    categoryId: "mitragynine",
    program: "Program A — Kratom / Harm Reduction",
    cas: null,
    formula: "TBD",
    molecularWeight: "TBD",
    schedulingStatus: "unscheduled",
    schedulingNote: "Novel research compound — not currently scheduled. Confirm pre-synthesis.",
    benefitScore: 75,
    harmScore: 25,
    evidenceConfidence: "LOW",
    status: "RESEARCH-NEEDED",
    statusColor: "orange",
    goDecision: "TODO — companion compound to MGM-9 pipeline",
    priorityTier: "Tier 1",
    summary: "MGM-15 is a fluorinated mitragynine analog in the MGM-series developed as a companion to MGM-9. Structurally distinct fluorination pattern intended to modulate CYP3A4 metabolism and tune MOR/DOR bias. Mentioned in Wohler opportunity deck alongside MGM-9 and MGM-16. Full literature citations pending retrieval.",
    pros: [
      "Companion to MGM-9 flagship synthesis",
      "Fluorination strategy may improve PK / metabolic stability vs mitragynine",
      "Same kg-scale solvent palette as MGM-9 (EtOAc, acetone, IPA)"
    ],
    cons: [
      "Literature coverage limited — structure details pending",
      "No published in vivo data yet"
    ],
    pharmacology: { targets: [{ receptor: "MOR", activity: "TBD", value: "Pending" }] },
    labSynthesis: null,
    kgScaleSynthesis: null,
    safety: { sideEffects: ["Pending research"] },
    publishedResearch: { keyFindings: ["Listed in Wohler internal opportunity deck alongside MGM-9, MGM-16, SC13"] },
    wohlerScoring: {
      benefitRationale: "High potential as MGM-9 companion; expected favorable biased-MOR profile",
      harmRationale: "Placeholder — requires in vitro safety screen",
      evidenceBasis: "LOW — limited published data"
    },
    nextSteps: [
      "Retrieve Takayama / Kruegel / Váradi MGM-series publications",
      "Confirm exact fluorination position (C-9, C-10, or C-12 aromatic)",
      "Draft synthesis from mitragynine oxalate + Selectfluor (parallel to MGM-9)",
      "Populate full data fields once structure is confirmed"
    ]
  },

  "mgm-16": {
    id: "mgm-16",
    name: "MGM-16",
    fullName: "MGM-16 (mitragynine analog — structure pending)",
    aliases: [],
    categoryId: "mitragynine",
    program: "Program A — Kratom / Harm Reduction",
    cas: null,
    formula: "TBD",
    molecularWeight: "TBD",
    schedulingStatus: "unscheduled",
    schedulingNote: "Novel — not scheduled. Confirm pre-synthesis.",
    benefitScore: 72,
    harmScore: 25,
    evidenceConfidence: "LOW",
    status: "RESEARCH-NEEDED",
    statusColor: "orange",
    goDecision: "TODO — MGM-series completion",
    priorityTier: "Tier 2",
    summary: "MGM-16 is the third compound in the fluorinated mitragynine analog series (after MGM-9 and MGM-15). Included in Wohler opportunity deck. Structure and published biology pending confirmation.",
    pros: ["Rounds out the MGM-series program", "Shares kg-scale feasibility with MGM-9"],
    cons: ["Very limited published data", "Structure uncertainty"],
    pharmacology: { targets: [] },
    labSynthesis: null,
    kgScaleSynthesis: null,
    safety: { sideEffects: ["Pending research"] },
    publishedResearch: { keyFindings: ["Listed in Wohler opportunity deck alongside MGM-9 and MGM-15"] },
    wohlerScoring: { benefitRationale: "Program completeness", harmRationale: "TBD", evidenceBasis: "LOW" },
    nextSteps: [
      "Confirm structure from Takayama/Kruegel MGM-series publications",
      "Populate full synthesis and pharmacology data once structure is known",
      "Decision gate after literature review"
    ]
  },

  "sc13": {
    id: "sc13",
    name: "SC13",
    fullName: "SC13 (mitragynine-derived opioid research tool)",
    aliases: [],
    categoryId: "mitragynine",
    program: "Program A — Kratom / Harm Reduction",
    cas: null,
    formula: "TBD",
    molecularWeight: "TBD",
    schedulingStatus: "unscheduled",
    schedulingNote: "Novel — unscheduled. Confirm.",
    benefitScore: 70,
    harmScore: 30,
    evidenceConfidence: "LOW",
    status: "RESEARCH-NEEDED",
    statusColor: "orange",
    goDecision: "TODO",
    priorityTier: "Tier 3",
    summary: "SC13 is a mitragynine-derived opioid receptor research tool mentioned in the Wohler opportunity deck. Included for program completeness. Structure and biology pending literature review.",
    pros: ["Expands SAR in MGM program"],
    cons: ["Minimal published data"],
    pharmacology: { targets: [] },
    labSynthesis: null, kgScaleSynthesis: null,
    safety: { sideEffects: ["Pending"] },
    publishedResearch: { keyFindings: ["Listed in Wohler opportunity deck"] },
    wohlerScoring: { benefitRationale: "TBD", harmRationale: "TBD", evidenceBasis: "LOW" },
    nextSteps: ["Retrieve SC13 literature", "Confirm structure", "Assess Go/Hold/Kill"]
  },

  // ========================================================================
  // OPIOID / BIASED MOR — Additional
  // ========================================================================
  "conolidine": {
    id: "conolidine",
    name: "Conolidine",
    fullName: "Conolidine (Tabernaemontana divaricata alkaloid)",
    aliases: [],
    categoryId: "opioid",
    program: "Program D — Biased MOR / Safer Opioid",
    cas: "6879-01-2",
    formula: "C₁₉H₂₄N₂O",
    molecularWeight: "296.41 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. Natural product from Tabernaemontana divaricata. Research-grade conolidine was first synthesized by Micalizio (2011) — prior scarcity of natural supply limited research.",
    benefitScore: 82,
    harmScore: 18,
    evidenceConfidence: "MODERATE",
    status: "RESEARCH-NEEDED",
    statusColor: "green",
    goDecision: "TODO — strong candidate for non-opioid analgesia program",
    priorityTier: "Tier 1",
    summary: "Conolidine is a C5-nor-stemmadenine alkaloid from Tabernaemontana divaricata with potent analgesic activity in mouse models without classical opioid receptor activity. Proposed mechanism includes ACKR3 (CXCR7) modulation and opioid-peptide activity enhancement. Represents an interesting non-MOR analgesic research tool.",
    pros: [
      "Potent analgesic in mouse hot-plate and tail-flick without classical opioid binding",
      "No respiratory depression or constipation reported in animal studies",
      "Novel mechanism — ACKR3/CXCR7 atypical chemokine receptor modulation",
      "Micalizio 2011 synthesis published in Nature (Tarselli et al.)"
    ],
    cons: [
      "Mechanism of action not fully established",
      "No human PK or clinical data",
      "Historically supply-limited (natural isolation)"
    ],
    pharmacology: {
      targets: [
        { receptor: "ACKR3 / CXCR7", activity: "Modulator (proposed)", value: "Mechanism from Meyrath 2020 Nat Commun" },
        { receptor: "Classical MOR/KOR/DOR", activity: "Inactive", value: "Ki > 10 µM" }
      ],
      mechanism: "Atypical chemokine receptor modulation — proposed non-opioid analgesic mechanism"
    },
    labSynthesis: null,
    kgScaleSynthesis: null,
    safety: { sideEffects: ["Minimal in rodent studies"], acuteToxicity: "Favorable profile in published animal work" },
    publishedResearch: {
      keyFindings: [
        "Tarselli et al. 2011 Nature Chem — total synthesis of (−)-conolidine",
        "Meyrath et al. 2020 Nature Communications — ACKR3 mechanism",
        "Review of conolidine analgesic pharmacology — Nature Rev Drug Disc 2021"
      ]
    },
    wohlerScoring: {
      benefitRationale: "Novel non-opioid analgesic mechanism + favorable animal safety",
      harmRationale: "No demonstrated abuse liability; mechanism still being characterized",
      evidenceBasis: "MODERATE — good preclinical data, no clinical"
    },
    nextSteps: [
      "Build full Micalizio 2011 synthesis writeup (Nature Chem)",
      "Translate synthesis to kg-scale using Wohler standard solvents",
      "Develop scoring with Meyrath 2020 ACKR3 mechanism data",
      "Regulatory attorney review (natural product, unscheduled)",
      "Identify commercial starting-material vendor for Tabernaemontana intermediates"
    ]
  },

  // ========================================================================
  // ENTACTOGENS — Additional
  // ========================================================================
  "heal": {
    id: "heal",
    name: "HEAL",
    fullName: "HEAL (MDMA-like research compound — structure pending public disclosure)",
    aliases: [],
    categoryId: "entactogens",
    program: "Program E — Entactogens",
    cas: null,
    formula: "TBD",
    molecularWeight: "TBD",
    schedulingStatus: "unscheduled",
    schedulingNote: "Novel research compound. Confirm scheduling status pre-synthesis via regulatory counsel.",
    benefitScore: 72,
    harmScore: 28,
    evidenceConfidence: "LOW",
    status: "RESEARCH-NEEDED",
    statusColor: "orange",
    goDecision: "TODO — research MDMA-adjacent entactogen alternative",
    priorityTier: "Tier 2",
    summary: "HEAL is a research-stage MDMA-adjacent entactogen discussed in the Wohler expansion list. It is presented as a companion or alternative to MEAI and R-MDMA in the entactogen lane. Full structure, pharmacology, and scheduling status require confirmation from primary literature.",
    pros: ["MDMA-alternative for AUD and PTSD research", "Potential lower abuse liability vs MDMA"],
    cons: ["Very limited published data", "Federal Analogue Act consideration if structure is too MDMA-like"],
    pharmacology: { targets: [{ receptor: "SERT / DAT / NET", activity: "TBD", value: "Pending structure disclosure" }] },
    labSynthesis: null, kgScaleSynthesis: null,
    safety: { sideEffects: ["Pending research"] },
    publishedResearch: { keyFindings: ["Listed in Wohler expansion category list (2026)"] },
    wohlerScoring: { benefitRationale: "MDMA alternative", harmRationale: "TBD", evidenceBasis: "LOW" },
    nextSteps: [
      "Confirm HEAL structure and primary reference source",
      "Literature review: compare to MEAI, R-MDMA, methylone",
      "Regulatory attorney review for Federal Analogue Act exposure",
      "Populate synthesis and kg-scale plan once structure is confirmed"
    ]
  },

  "r-mdma": {
    id: "r-mdma",
    name: "R-MDMA",
    fullName: "(R)-(−)-3,4-Methylenedioxymethamphetamine",
    aliases: ["R-(-)-MDMA"],
    categoryId: "entactogens",
    program: "Program E — Entactogens",
    cas: "81262-70-6",
    formula: "C₁₁H₁₅NO₂",
    molecularWeight: "193.24 g/mol",
    schedulingStatus: "scheduled",
    schedulingNote: "SCHEDULE I — both R and S enantiomers of MDMA are DEA Schedule I. Only synthesizable under DEA Schedule I researcher license with institutional partnership. Watch-only pipeline compound.",
    benefitScore: 60,
    harmScore: 65,
    evidenceConfidence: "MODERATE",
    status: "HOLD",
    statusColor: "red",
    goDecision: "HOLD — Schedule I. Reference only.",
    priorityTier: "Watch",
    summary: "R-MDMA is the (R)-enantiomer of MDMA. Animal studies suggest reduced locomotor stimulation and serotonergic neurotoxicity compared to racemic MDMA while preserving prosocial / entactogenic effects (Pitts 2018). Cannot be produced without DEA Schedule I license. Tracked as a reference compound only.",
    pros: [
      "Reduced serotonergic neurotoxicity vs racemic MDMA in animal studies (Pitts 2018)",
      "Maintains MDMA-like prosocial effects at lower neurotoxic cost",
      "Pure-enantiomer research relevance for MAPS-adjacent Phase 2/3 exploration"
    ],
    cons: [
      "Schedule I — cannot produce or ship without DEA license",
      "Limits Wohler's supply role to reference only"
    ],
    pharmacology: {
      targets: [
        { receptor: "SERT", activity: "Releaser (weaker than S)", value: "EC50 ~ 1 µM" },
        { receptor: "DAT / NET", activity: "Releaser (weak)", value: "vs S-MDMA: 5-10× weaker DA release" }
      ],
      mechanism: "Monoamine releaser with preferential SERT > DAT activity"
    },
    labSynthesis: null, kgScaleSynthesis: null,
    safety: { sideEffects: ["Reduced neurotoxicity vs racemic MDMA", "Still subject to classical MDMA warnings"] },
    publishedResearch: { keyFindings: ["Pitts et al. 2018 — enantiomeric pharmacology of MDMA"] },
    wohlerScoring: { benefitRationale: "Lower neurotoxicity profile", harmRationale: "Schedule I restricts all research logistics", evidenceBasis: "MODERATE" },
    nextSteps: [
      "Monitor DEA Schedule I research-license feasibility",
      "Watch Lykos / MAPS clinical program for regulatory shifts",
      "No synthesis work — keep on reference / watch list only"
    ]
  },

  // ========================================================================
  // COGNITIVE / WAKEFULNESS — Additional
  // ========================================================================
  "adrafinil": {
    id: "adrafinil",
    name: "Adrafinil",
    fullName: "(±)-2-[(diphenylmethyl)sulfinyl]-N-hydroxyacetamide",
    aliases: ["Olmifon", "CRL-40028"],
    categoryId: "cognitive",
    program: "Program C — Wakefulness / Neuroinflammation",
    cas: "63547-13-7",
    formula: "C₁₅H₁₅NO₃S",
    molecularWeight: "289.35 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled in the U.S. (modafinil is Schedule IV). Sold as research chemical / supplement in the U.S. but discontinued in France 2011. Research-use-only posture required for supply.",
    benefitScore: 65,
    harmScore: 32,
    evidenceConfidence: "MODERATE",
    status: "RESEARCH-NEEDED",
    statusColor: "orange",
    goDecision: "TODO — useful modafinil prodrug reference for Program C",
    priorityTier: "Tier 2",
    summary: "Adrafinil is a prodrug for modafinil — metabolized hepatically to yield modafinil + modafinilic acid. Historically the first wakefulness compound in this class (Lafon, 1970s) and marketed as Olmifon in France for narcolepsy until 2011. Adrafinil is NOT federally scheduled (unlike modafinil, Schedule IV). Liver loading is higher than modafinil due to hepatic conversion — this is the main safety consideration vs modafinil.",
    pros: [
      "Not federally scheduled (vs modafinil Schedule IV)",
      "Established prodrug PK — predictable modafinil exposure",
      "Well-characterized in animal + human narcolepsy studies (1980s-1990s)"
    ],
    cons: [
      "Elevated hepatic enzymes with chronic use — France withdrew in 2011 over hepatotoxicity concerns",
      "~2× loading dose vs modafinil to achieve same modafinil Cmax",
      "Slower onset than modafinil (hepatic first-pass conversion required)"
    ],
    pharmacology: {
      targets: [
        { receptor: "DAT", activity: "Weak inhibitor (via modafinil metabolite)", value: "Modafinil Ki ~ 3.7 µM" },
        { receptor: "Histamine / Orexin circuits", activity: "Indirect activation", value: "Via modafinil" }
      ],
      mechanism: "Hepatic prodrug — hydrolyzed to modafinil (active) and modafinilic acid (inactive); modafinil promotes wakefulness via DAT inhibition + orexin/histamine circuit activation"
    },
    labSynthesis: null,
    kgScaleSynthesis: null,
    safety: {
      sideEffects: ["Elevated hepatic enzymes (AST/ALT) with chronic use", "Headache", "Nausea", "Insomnia", "Anxiety"],
      acuteToxicity: "LD50 (rat, oral) ~ 1300 mg/kg — low acute toxicity",
      chronicRisks: "Hepatotoxicity with chronic use — reason for 2011 Lafon market withdrawal in France",
      contraindications: ["Severe hepatic impairment", "Concurrent hepatotoxic drugs"]
    },
    publishedResearch: {
      keyFindings: [
        "Milgram 2000 — adrafinil in aged dogs, cognitive enhancement",
        "Bastuji & Jouvet 1988 — narcolepsy treatment",
        "Lafon Olmifon withdrawal 2011 — hepatotoxicity risk profile"
      ]
    },
    wohlerScoring: {
      benefitRationale: "Unscheduled wakefulness research tool with known prodrug PK",
      harmRationale: "Hepatotoxicity with chronic use is a real, documented concern",
      evidenceBasis: "MODERATE — decades of clinical use data available"
    },
    nextSteps: [
      "Build full sulfoxide synthesis from benzhydrylthioacetic acid + H2O2/MCPBA",
      "Translate to kg-scale using EtOAc + ethanol + acetone palette",
      "Draft SOP for hepatic-safety warnings for research customers",
      "Compile full publication list (Milgram, Bastuji) with DOIs"
    ]
  },

  // ========================================================================
  // ED / SEXUAL HEALTH — Additional
  // ========================================================================
  "amino-tadalafil": {
    id: "amino-tadalafil",
    name: "Amino Tadalafil",
    fullName: "Aminotadalafil — research-grade tadalafil analog",
    aliases: ["Tadalafil impurity B", "Aminotadalafil"],
    categoryId: "ed-sexual",
    program: "Program G — ED / Sexual Health",
    cas: "385769-84-6",
    formula: "C₂₁H₁₉N₃O₄",
    molecularWeight: "377.39 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. Classified as an adulterant in the FDA Tainted Sexual Enhancement Products list (Feb 2020) — cannot be sold as a dietary supplement in the U.S. Pure research-chemical status maintained only with research-use-only labeling and documented IRB/university partnerships.",
    benefitScore: 72,
    harmScore: 22,
    evidenceConfidence: "HIGH",
    status: "RESEARCH-NEEDED",
    statusColor: "green",
    goDecision: "TODO — high-value research PDE5 reference compound",
    priorityTier: "Tier 1",
    summary: "Aminotadalafil is a structural analog of tadalafil — a well-characterized PDE5 inhibitor research compound and tadalafil impurity reference standard. Published IC50 values against PDE5 (Lebret 2003) demonstrate comparable potency to tadalafil itself, making it an excellent research reference for PDE5 pharmacology studies. FDA has flagged it as an adulterant in supplements, so Wohler must enforce strict research-use-only posture.",
    pros: [
      "Well-characterized PDE5 IC50 (~1.5–3 nM — comparable to tadalafil)",
      "Official tadalafil impurity reference standard — pharmacopeial interest",
      "Unscheduled — facilitates research supply",
      "High demand from analytical chemistry labs (tadalafil QC)"
    ],
    cons: [
      "FDA flagged as supplement adulterant — Wohler must enforce research-use-only labeling",
      "Off-label human consumption risk if product is diverted",
      "Potential FDA scrutiny of supplier reputation"
    ],
    pharmacology: {
      targets: [
        { receptor: "PDE5", activity: "Inhibitor", value: "IC50 ~ 1.5–3 nM (Lebret 2003)" },
        { receptor: "PDE6", activity: "Weak inhibitor (selectivity ~700×)", value: "IC50 ~ 2 µM" }
      ],
      mechanism: "Competitive PDE5 inhibitor — identical mechanism to tadalafil (blocks cGMP hydrolysis → smooth muscle relaxation in corpus cavernosum)"
    },
    labSynthesis: null,
    kgScaleSynthesis: null,
    safety: {
      sideEffects: ["Headache", "Flushing", "Dyspepsia", "Myalgia (class effect)", "Hypotension with nitrates (class warning)"],
      acuteToxicity: "Favorable — analogous to tadalafil",
      contraindications: ["Concurrent organic nitrates — risk of severe hypotension", "Severe hepatic / renal impairment"]
    },
    publishedResearch: {
      keyFindings: [
        "Lebret et al. 2003 — J Med Chem SAR of tadalafil analogs",
        "Venhuis et al. 2008 — Anal Bioanal Chem, characterization in supplements",
        "FDA Tainted Sexual Enhancement Products database (ongoing)"
      ]
    },
    wohlerScoring: {
      benefitRationale: "High research demand + well-characterized PDE5 reference compound",
      harmRationale: "Moderate — FDA adulterant status requires rigorous labeling/SOP discipline",
      evidenceBasis: "HIGH — extensive published SAR + analytical data"
    },
    nextSteps: [
      "Build Lebret 2003 synthesis (Pictet-Spengler from D-tryptophan methyl ester)",
      "Translate to kg-scale using EtOAc + DCM-alt (Wohler uses EtOAc + THF + acetone palette)",
      "Draft research-use-only labeling SOP with regulatory attorney",
      "Confirm unscheduled status in all target states",
      "Identify D-tryptophan methyl ester vendor / reliable supply"
    ]
  },

  "thio-tadalafil": {
    id: "thio-tadalafil",
    name: "Thio-Tadalafil",
    fullName: "Thiotadalafil — sulfur-substituted tadalafil analog",
    aliases: ["Thiotadalafil"],
    categoryId: "ed-sexual",
    program: "Program G — ED / Sexual Health",
    cas: "171596-36-4",
    formula: "C₂₂H₁₉N₃O₃S",
    molecularWeight: "405.47 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. Known PDE5 adulterant — FDA Tainted Sexual Enhancement Products list (2009+). Research-use-only posture required.",
    benefitScore: 68,
    harmScore: 28,
    evidenceConfidence: "MODERATE",
    status: "RESEARCH-NEEDED",
    statusColor: "orange",
    goDecision: "TODO — PDE5 SAR research tool",
    priorityTier: "Tier 2",
    summary: "Thiotadalafil replaces the dione carbonyl oxygens of tadalafil with sulfur (thione) to give a sulfur-substituted research analog. Potent PDE5 inhibitor with published IC50 data (Venhuis 2008). Primarily a research reference compound for tadalafil SAR studies and an analytical reference for supplement-adulteration testing.",
    pros: [
      "PDE5 IC50 potent and published (Venhuis 2008)",
      "Key analytical reference for dietary supplement QC labs",
      "Unscheduled"
    ],
    cons: [
      "Adulterant listing by FDA requires strict SOP",
      "Lower clinical relevance than amino tadalafil (fewer pharmacopoeia references)"
    ],
    pharmacology: {
      targets: [
        { receptor: "PDE5", activity: "Inhibitor", value: "IC50 low-nM range (Venhuis 2008)" }
      ],
      mechanism: "PDE5 inhibitor — thione-for-ketone analog of tadalafil"
    },
    labSynthesis: null, kgScaleSynthesis: null,
    safety: { sideEffects: ["Class-effect PDE5 inhibitor profile"], contraindications: ["Organic nitrates"] },
    publishedResearch: { keyFindings: ["Venhuis et al. 2008 — Thiotadalafil characterization in adulterated supplements"] },
    wohlerScoring: { benefitRationale: "Research reference", harmRationale: "Moderate diversion risk", evidenceBasis: "MODERATE" },
    nextSteps: [
      "Review Venhuis 2008 synthesis route",
      "Design thionation step from tadalafil or direct synthesis from D-Trp-OMe + Lawesson reagent",
      "Draft kg-scale route using Wohler standard solvents",
      "Confirm unscheduled status with regulatory attorney"
    ]
  },

  "sildenafil-analogs": {
    id: "sildenafil-analogs",
    name: "Sildenafil / Vardenafil Research Analogs",
    fullName: "Research-grade non-approved sildenafil and vardenafil structural variants",
    aliases: ["PDE5 research analogs"],
    categoryId: "ed-sexual",
    program: "Program G — ED / Sexual Health",
    cas: null,
    formula: "Various (PDE5 inhibitor pyrazolopyrimidine / triazine scaffolds)",
    molecularWeight: "~450–550 g/mol (analog-dependent)",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. Many members are FDA Tainted Sexual Enhancement Products list adulterants (e.g., acetildenafil, hydroxyhomosildenafil, aildenafil, dimethylsildenafil). Research-use-only posture required.",
    benefitScore: 65,
    harmScore: 28,
    evidenceConfidence: "MODERATE",
    status: "RESEARCH-NEEDED",
    statusColor: "orange",
    goDecision: "TODO — broad PDE5 SAR program",
    priorityTier: "Tier 2",
    summary: "A class of research-grade sildenafil and vardenafil structural analogs used in analytical reference, PDE5 SAR studies, and supplement QC. Includes acetildenafil, hydroxyhomosildenafil, dimethylsildenafil, and related pyrazolopyrimidine/triazine scaffolds. Many are documented FDA supplement adulterants, which means Wohler's role is strictly research-reference with rigorous research-use-only labeling.",
    pros: [
      "High analytical-lab demand for reference standards",
      "Documented PDE5 SAR published for most class members",
      "Unscheduled federal status"
    ],
    cons: [
      "Repeatedly found in adulterated supplements — reputational care required",
      "Each analog requires separate synthesis + QC infrastructure"
    ],
    pharmacology: {
      targets: [
        { receptor: "PDE5", activity: "Inhibitor (varies by analog)", value: "IC50 typically 1–50 nM" }
      ],
      mechanism: "PDE5 inhibition — class mechanism shared with sildenafil/vardenafil"
    },
    labSynthesis: null, kgScaleSynthesis: null,
    safety: { sideEffects: ["Class PDE5 effects"], contraindications: ["Nitrates"] },
    publishedResearch: { keyFindings: ["Venhuis 2007-2011 series", "FDA Tainted Products list"] },
    wohlerScoring: { benefitRationale: "Broad analytical reference demand", harmRationale: "Adulterant associations", evidenceBasis: "MODERATE" },
    nextSteps: [
      "Prioritize 3 analogs: acetildenafil, hydroxyhomosildenafil, dimethylsildenafil",
      "Literature review for each (Venhuis, Reepmeyer series)",
      "Build individual synthesis procedures",
      "Regulatory attorney review of research-use-only labeling"
    ]
  },

  // ========================================================================
  // ENDOCANNABINOID / CB2 — NEW CATEGORY
  // ========================================================================
  "cbd-research-grade": {
    id: "cbd-research-grade",
    name: "Research-Grade CBD",
    fullName: "(−)-Cannabidiol (research-grade synthetic or hemp-derived isolate)",
    aliases: ["CBD"],
    categoryId: "cannabinoid",
    program: "Program K — Endocannabinoid / CB2",
    cas: "13956-29-1",
    formula: "C₂₁H₃₀O₂",
    molecularWeight: "314.47 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Hemp-derived CBD (<0.3% THC) is federally legal under 2018 Farm Bill. State laws vary. Epidiolex (pharmaceutical CBD) is FDA-approved and was removed from DEA schedules in 2020. Research-use-only labeling recommended.",
    benefitScore: 80,
    harmScore: 12,
    evidenceConfidence: "HIGH",
    status: "RESEARCH-NEEDED",
    statusColor: "green",
    goDecision: "TODO — flagship compound for new cannabinoid program",
    priorityTier: "Tier 1",
    summary: "Research-grade cannabidiol (CBD) — the non-intoxicating phytocannabinoid with extensive published pharmacology across epilepsy, anxiety, inflammation, and pain research. Epidiolex (FDA-approved) has validated the therapeutic profile. Wohler research-grade supply would support academic programs in neuroinflammation, CB2 signaling, and non-opioid pain research.",
    pros: [
      "FDA-approved indication (Dravet, Lennox-Gastaut, TSC) validates safety/efficacy",
      "Extensive published pharmacology — low-barrier academic research",
      "Broad mechanism: CB1 negative allosteric modulator, 5-HT1A, TRPV1, GPR55, PPARγ",
      "Low abuse liability — favorable for grad-school program",
      "High student demand — easy to onboard university partners"
    ],
    cons: [
      "Market saturated — differentiation via purity/COA rigor required",
      "Quality-control requirements are strict (THC <0.3%, heavy metals, pesticides)"
    ],
    pharmacology: {
      targets: [
        { receptor: "CB1", activity: "Negative allosteric modulator", value: "Does not directly activate CB1 orthosteric site" },
        { receptor: "CB2", activity: "Weak agonist / inverse agonist", value: "Context-dependent" },
        { receptor: "5-HT1A", activity: "Agonist", value: "Ki ~ 16 µM" },
        { receptor: "TRPV1", activity: "Agonist", value: "EC50 ~ 1 µM" },
        { receptor: "GPR55", activity: "Antagonist", value: "IC50 ~ 0.5 µM" },
        { receptor: "PPARγ", activity: "Agonist", value: "EC50 ~ 5 µM" }
      ],
      mechanism: "Polypharmacological — modulates endocannabinoid tone without direct CB1 orthosteric activation"
    },
    labSynthesis: null, kgScaleSynthesis: null,
    safety: {
      sideEffects: ["Somnolence", "Diarrhea", "Decreased appetite", "Elevated LFTs at high dose"],
      acuteToxicity: "Very favorable — LD50 > 2000 mg/kg in rodents",
      chronicRisks: "Hepatic enzyme induction — monitor at chronic high doses (>10 mg/kg/day)"
    },
    publishedResearch: {
      keyFindings: [
        "Devinsky et al. 2017 NEJM — Dravet syndrome Phase 3",
        "Bergamaschi 2011 Neuropsychopharm — social anxiety",
        "Philpott 2017 — neuroinflammation review",
        "Epidiolex prescribing information (GW Pharmaceuticals)"
      ]
    },
    wohlerScoring: {
      benefitRationale: "Validated therapeutic compound with vast academic demand",
      harmRationale: "Very low — decades of safety data",
      evidenceBasis: "HIGH"
    },
    nextSteps: [
      "Develop hemp-derived isolate purification SOP (CO2 + ethanol extraction + chromatography)",
      "OR develop synthetic route from olivetol + (+)-p-mentha-2,8-dien-1-ol",
      "Build kg-scale plan using Wohler standard solvents (ethanol + heptane)",
      "QC: HPLC assay + pesticide panel + heavy metals + residual solvents",
      "COA template for university partners"
    ]
  },

  "cbg-research-grade": {
    id: "cbg-research-grade",
    name: "Research-Grade CBG",
    fullName: "Cannabigerol",
    aliases: ["CBG"],
    categoryId: "cannabinoid",
    program: "Program K — Endocannabinoid / CB2",
    cas: "25654-31-3",
    formula: "C₂₁H₃₂O₂",
    molecularWeight: "316.48 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Hemp-derived CBG is federally legal under 2018 Farm Bill. Not federally scheduled. Research-use-only posture.",
    benefitScore: 75,
    harmScore: 15,
    evidenceConfidence: "MODERATE",
    status: "RESEARCH-NEEDED",
    statusColor: "green",
    goDecision: "TODO — complementary non-psychoactive cannabinoid",
    priorityTier: "Tier 2",
    summary: "Cannabigerol (CBG) is the 'parent' phytocannabinoid — biosynthetic precursor to THC, CBD, and CBC. Non-intoxicating with promising preclinical data in IBD, glaucoma, and MRSA (CB2 agonism + α2-adrenergic binding). Research-grade supply would support neuroinflammation and IBD research programs.",
    pros: [
      "Non-intoxicating — low abuse liability",
      "Promising IBD preclinical data (Borrelli 2013)",
      "α2-adrenergic agonism adds differentiation vs CBD",
      "Growing market demand in cannabinoid research"
    ],
    cons: [
      "Lower published clinical data volume than CBD",
      "Biosynthesis from CBGA requires careful decarboxylation control"
    ],
    pharmacology: {
      targets: [
        { receptor: "CB1", activity: "Weak partial agonist", value: "Ki ~ 440 nM" },
        { receptor: "CB2", activity: "Partial agonist", value: "Ki ~ 340 nM" },
        { receptor: "α2-adrenergic", activity: "Agonist", value: "Ki ~ 200 nM" },
        { receptor: "5-HT1A", activity: "Antagonist", value: "Ki ~ 51.9 µM" },
        { receptor: "TRPV1/A1/M8", activity: "Modulator", value: "Low µM" }
      ],
      mechanism: "Mixed cannabinoid + α2-adrenergic + TRP channel activity"
    },
    labSynthesis: null, kgScaleSynthesis: null,
    safety: { sideEffects: ["Dry mouth", "Somnolence (rare)"], acuteToxicity: "Favorable — analogous to CBD" },
    publishedResearch: { keyFindings: ["Borrelli 2013 — IBD preclinical", "Valdeolivas 2015 — Huntington's preclinical", "Appendino 2008 — MRSA activity"] },
    wohlerScoring: { benefitRationale: "Complementary to CBD", harmRationale: "Very low", evidenceBasis: "MODERATE" },
    nextSteps: [
      "Extraction/isolation SOP from hemp (high-CBG cultivars now available)",
      "Synthetic route: olivetol + geraniol condensation",
      "kg-scale plan with ethanol + heptane palette",
      "QC + COA template"
    ]
  },

  "hu-308": {
    id: "hu-308",
    name: "HU-308",
    fullName: "[(1R,2R,5R)-2-[2,6-dimethoxy-4-(2-methyloctan-2-yl)phenyl]-7,7-dimethyl-4-bicyclo[3.1.1]hept-3-enyl]methanol",
    aliases: [],
    categoryId: "cannabinoid",
    program: "Program K — Endocannabinoid / CB2",
    cas: "256934-39-1",
    formula: "C₂₇H₄₂O₃",
    molecularWeight: "414.62 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Selective CB2 agonist research compound. Not federally scheduled. Synthetic cannabinoid but with low/no CB1 activity — distinct from Schedule I synthetic cannabinoids (e.g., JWH series). Research-use-only.",
    benefitScore: 72,
    harmScore: 22,
    evidenceConfidence: "HIGH",
    status: "RESEARCH-NEEDED",
    statusColor: "green",
    goDecision: "TODO — gold-standard CB2-selective research tool",
    priorityTier: "Tier 1",
    summary: "HU-308 is a highly selective CB2 agonist (>400× CB2 over CB1) developed by the Mechoulam lab (Hebrew University). The gold-standard research tool for CB2-specific pharmacology studies, especially in neuroinflammation, bone remodeling, and liver ischemia research. High academic demand.",
    pros: [
      ">400× CB2/CB1 selectivity — clean pharmacology",
      "Well-characterized in vitro and in vivo",
      "Mechoulam-derived — strong academic credibility",
      "Strong bone / inflammation / ischemia preclinical data"
    ],
    cons: [
      "Complex synthesis (pinene-derived bicyclic scaffold)",
      "Expensive starting materials",
      "Niche demand (pure research tool, not therapeutic candidate)"
    ],
    pharmacology: {
      targets: [
        { receptor: "CB2", activity: "Full agonist", value: "Ki ≈ 22 nM" },
        { receptor: "CB1", activity: "Very weak", value: "Ki > 10 µM (>400× selectivity)" }
      ],
      mechanism: "Selective CB2 orthosteric agonist"
    },
    labSynthesis: null, kgScaleSynthesis: null,
    safety: { sideEffects: ["Minimal in rodent studies — non-psychoactive"], acuteToxicity: "Favorable at research doses" },
    publishedResearch: {
      keyFindings: [
        "Hanus et al. 1999 PNAS — discovery paper (Mechoulam lab)",
        "Ofek 2006 — bone remodeling",
        "Batkai 2007 — hepatic ischemia-reperfusion"
      ]
    },
    wohlerScoring: {
      benefitRationale: "Gold-standard CB2 research tool, strong academic demand",
      harmRationale: "Low — non-psychoactive CB2-selective",
      evidenceBasis: "HIGH"
    },
    nextSteps: [
      "Review Hanus 1999 PNAS synthesis — (+)-α-pinene + alkylresorcinol coupling",
      "Translate to kg scale (heptane + EtOAc palette)",
      "Source (+)-α-pinene + 2,6-dimethoxy-4-(2-methyl-2-octyl)resorcinol",
      "Cost analysis — currently $200-500/g at small scale"
    ]
  },

  "jwh-133": {
    id: "jwh-133",
    name: "JWH-133",
    fullName: "(6aR,10aR)-3-(1,1-dimethylbutyl)-6a,7,10,10a-tetrahydro-6,6,9-trimethyl-6H-dibenzo[b,d]pyran",
    aliases: [],
    categoryId: "cannabinoid",
    program: "Program K — Endocannabinoid / CB2",
    cas: "259869-55-1",
    formula: "C₂₂H₃₂O",
    molecularWeight: "312.49 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "CAUTION: Other JWH-series compounds (JWH-018, JWH-073, JWH-200) are federally scheduled (Schedule I, CSA 2012). JWH-133 is a CB2-selective analog and is NOT scheduled federally but should be confirmed state-by-state. Extreme care with research-use-only labeling.",
    benefitScore: 65,
    harmScore: 35,
    evidenceConfidence: "HIGH",
    status: "RESEARCH-NEEDED",
    statusColor: "orange",
    goDecision: "TODO — confirm state scheduling before proceeding",
    priorityTier: "Tier 3",
    summary: "JWH-133 is a CB2-selective cannabinoid agonist (~200× CB2 over CB1) from the John W. Huffman research series. Unlike the scheduled JWH-018/073/200 compounds, JWH-133 remains unscheduled federally due to its selective CB2 activity (minimal CB1 → no THC-like cannabimimetic). Research tool for CB2 studies — but reputational care is required given the JWH family's scheduling history.",
    pros: [
      "High CB2 selectivity (~200×) — clean CB2 pharmacology tool",
      "Unscheduled despite being in the JWH series (CB2-selective = no cannabimimetic)",
      "Strong published anti-inflammatory / bone data"
    ],
    cons: [
      "Shares naming convention with scheduled JWH compounds — reputational risk",
      "State-by-state scheduling confirmation required",
      "Analog Act consideration if misused"
    ],
    pharmacology: {
      targets: [
        { receptor: "CB2", activity: "Full agonist", value: "Ki ≈ 3.4 nM" },
        { receptor: "CB1", activity: "Weak", value: "Ki ≈ 677 nM (~200× selectivity)" }
      ],
      mechanism: "Selective CB2 orthosteric agonist — cannabimimetic-free"
    },
    labSynthesis: null, kgScaleSynthesis: null,
    safety: { sideEffects: ["Non-psychoactive at normal research doses"], acuteToxicity: "Favorable at preclinical doses" },
    publishedResearch: { keyFindings: ["Huffman 1999 discovery paper", "Ofek 2006 bone remodeling", "Ramirez 2012 neuroinflammation"] },
    wohlerScoring: {
      benefitRationale: "Complementary CB2 tool to HU-308",
      harmRationale: "Naming family has scheduling history — elevated regulatory optics",
      evidenceBasis: "HIGH"
    },
    nextSteps: [
      "Regulatory attorney review — confirm unscheduled status in every target state",
      "Draft synthesis from resorcinol + p-mentha-2,8-dien-1-ol",
      "kg-scale plan — EtOAc + heptane palette",
      "Decide whether to keep or drop based on reputational assessment"
    ]
  }

};